"use client";

import { SignUp, SignUpButton, useSignUp, useUser } from "@clerk/nextjs";
import { cloneElement, useEffect, type ComponentProps, type ReactElement, type MouseEvent } from "react";
import { clickSignupCta, openSignup } from "@/helpers/signupTracking";

type ButtonProps = Omit<ComponentProps<typeof SignUpButton>, "children" | "mode"> & {
  mode: "modal";
  placement: string;
  children: ReactElement<{ onClick?: (event: MouseEvent) => void | Promise<void> }>;
};

export function TrackedSignUpButton({ children, placement, ...props }: ButtonProps) {
  return <SignUpButton {...props}>{cloneElement(children, {
    onClick: async (event: MouseEvent) => {
      await children.props.onClick?.(event);
      if (event.defaultPrevented) return;
      clickSignupCta(placement);
      openSignup({ page_path: window.location.pathname });
    },
  })}</SignUpButton>;
}

export function TrackedSignUp(props: ComponentProps<typeof SignUp>) {
  const { isLoaded, isSignedIn } = useUser();
  const { signUp } = useSignUp();
  useEffect(() => {
    // Signup can complete before Clerk exposes the new active user. A route
    // remount during that transition must not reopen the completed journey.
    if (isLoaded && !isSignedIn && signUp?.status !== "complete") {
      openSignup({ page_path: window.location.pathname });
    }
  }, [isLoaded, isSignedIn, signUp?.status]);
  return <SignUp {...props} />;
}
